import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, OnInit, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {TranslateModule} from '@ngx-translate/core';
import {FeltValidatorer} from 'felles-ng2/infrastruktur/angular/feltValidatorer';
import {Klokke} from 'felles-ng2/tjenester/Klokke';
import {TemporalFormatering} from 'felles-ng2/tjenester/TemporalFormatering';
import {Meldingstype, OpprettSystemMeldingSpesifikasjon, SystemMeldingModell, SystemmeldingRessursClient, Varseltype} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {varighetIMinutter} from 'felles-ng2/støttefunksjoner/temporal';
import {Temporal} from 'temporal-polyfill';
import {RiktekstRedigeringKomponent} from 'web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering';
import {AdministrasjonNavigasjonKomponent} from '../administrasjon-navigasjon/administrasjon-navigasjon';
import {AdministrasjonMeldingKomponent} from '../melding/melding';

const varighetDetteErNyttMelding = 60 * 24 * 30 * 2;

@Component({
    selector: 'administrasjon-meldinger',
    templateUrl: 'administrasjon-meldinger.html',
    styleUrl: 'administrasjon-meldinger.scss',
    host: {'data-testid': 'administrasjon-meldinger'},
    imports: [
        ReactiveFormsModule,
        MatButton,
        MatFormField,
        MatInput,
        MatLabel,
        MatOption,
        MatSelect,
        TranslateModule,
        RiktekstRedigeringKomponent,
        AdministrasjonNavigasjonKomponent,
        AdministrasjonMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonMeldingerKomponent implements OnInit {
    static ajsSelector = 'administrasjonMeldinger';

    readonly meldinger = signal<SystemMeldingModell[]>([]);
    readonly aktiveMeldinger = computed(() => this.meldinger().filter(melding => this.erMeldingGyldigOgIkkeMal(melding)));
    readonly utlopteMeldinger = computed(() => this.meldinger().filter(melding => this.erMeldingUtløptOgIkkeMal(melding)));
    readonly malMeldinger = computed(() => this.meldinger().filter(melding => melding.meldingstype === Meldingstype.mal));

    readonly varseltyper = [
        {verdi: Varseltype.info, navn: 'Info'},
        {verdi: Varseltype.advarsel, navn: 'Advarsel'},
        {verdi: Varseltype.feil, navn: 'Feil'}
    ];
    readonly meldingstyper = [
        Meldingstype.loggInnBankId,
        Meldingstype.loggInnHoved,
        Meldingstype.applikasjonHoved,
        Meldingstype.mal,
        Meldingstype.detteErNyttForMedlem,
        Meldingstype.detteErNyttForAdmin,
        Meldingstype.detteErNyttForTesting
    ].map(verdi => ({verdi, navn: this.meldingstypeNavn(verdi)}));
    readonly varigheter: Array<{verdi: number | null, navn: string}> = [30, 60, 90, 60 * 2, 60 * 3, 60 * 10, 60 * 24, 60 * 24 * 5]
      .map(verdi => ({verdi, navn: this.temporalFormatering.formaterDuration(varighetIMinutter(verdi))}))
      .concat({verdi: null, navn: 'For evig'});

    readonly skjema = new FormGroup({
        meldingstype: new FormControl<Meldingstype | null>(null, {validators: Validators.required}),
        varseltype: new FormControl<Varseltype | null>(null, {validators: Validators.required}),
        varighetIMinutter: new FormControl<number | null>(null),
        melding: new FormControl<string | null>('', {validators: FeltValidatorer.htmlTekstPåkrevet})
    });

    constructor(private avhengigheter: AdministrasjonMeldingerAvhengigheter,
                private temporalFormatering: TemporalFormatering,
                private klokke: Klokke,
                destroyRef: DestroyRef) {
        this.skjema.controls.meldingstype.valueChanges
          .pipe(takeUntilDestroyed(destroyRef))
          .subscribe(() => this.oppdaterValideringForValgtMeldingstype());
    }

    ngOnInit(): void {
        void this.lastMeldinger();
    }

    erDetteErNyttMelding(): boolean {
        return detteErNyttMelding(this.skjema.controls.meldingstype.value);
    }

    async lagre(): Promise<void> {
        if (this.skjema.invalid) {
            this.skjema.markAllAsTouched();
            return;
        }

        await this.avhengigheter.opprett(this.somSpesifikasjon());
        await this.lastMeldinger();
        this.skjema.reset();
        this.oppdaterValideringForValgtMeldingstype();
    }

    async vedMeldingSlettet(): Promise<void> {
        await this.lastMeldinger();
    }

    vedOpprettFraMal(melding: SystemMeldingModell): void {
        this.skjema.patchValue({
            meldingstype: melding.meldingstype,
            varseltype: melding.varseltype,
            varighetIMinutter: null,
            melding: detteErNyttMelding(melding.meldingstype) ? dekodHexHtmlEntiteter(melding.melding) : melding.melding
        });
        this.oppdaterValideringForValgtMeldingstype();
        this.skjema.markAsPristine();
        this.skjema.markAsUntouched();
    }

    private async lastMeldinger(): Promise<void> {
        this.meldinger.set(await this.avhengigheter.finnAlle());
    }

    private oppdaterValideringForValgtMeldingstype(): void {
        const detteErNytt = this.erDetteErNyttMelding();
        this.skjema.controls.varseltype.setValidators(detteErNytt ? null : Validators.required);
        this.skjema.controls.melding.setValidators(detteErNytt ? Validators.required : FeltValidatorer.htmlTekstPåkrevet);
        this.skjema.controls.varseltype.updateValueAndValidity({emitEvent: false});
        this.skjema.controls.melding.updateValueAndValidity({emitEvent: false});
    }

    private somSpesifikasjon(): OpprettSystemMeldingSpesifikasjon {
        const meldingstype = this.skjema.controls.meldingstype.value!;
        const detteErNytt = detteErNyttMelding(meldingstype);
        return {
            meldingstype,
            varseltype: detteErNytt ? Varseltype.info : this.skjema.controls.varseltype.value!,
            melding: this.skjema.controls.melding.value!,
            // API-et tillater null for meldinger som skal vare evig, selv om den genererte typen mangler nullabiliteten.
            varighetIMinutter: (detteErNytt ? varighetDetteErNyttMelding : this.skjema.controls.varighetIMinutter.value) as number
        };
    }

    private erMeldingGyldigOgIkkeMal(melding: SystemMeldingModell): boolean {
        return melding.meldingstype !== Meldingstype.mal &&
          (melding.gyldigTil === null || Temporal.Instant.compare(Temporal.Instant.from(melding.gyldigTil), this.klokke.nåSomZonedDateTime().toInstant()) >= 0);
    }

    private erMeldingUtløptOgIkkeMal(melding: SystemMeldingModell): boolean {
        return melding.meldingstype !== Meldingstype.mal && melding.gyldigTil !== null &&
          Temporal.Instant.compare(Temporal.Instant.from(melding.gyldigTil), this.klokke.nåSomZonedDateTime().toInstant()) < 0;
    }

    private meldingstypeNavn(meldingstype: Meldingstype): string {
        switch (meldingstype) {
            case Meldingstype.mal: return 'Mal';
            case Meldingstype.loggInnBankId: return 'Innlogging med BankID';
            case Meldingstype.loggInnHoved: return 'Innlogging';
            case Meldingstype.applikasjonHoved: return 'Generell melding i applikasjon';
            case Meldingstype.detteErNyttForMedlem: return 'Dette er nytt for medlem';
            case Meldingstype.detteErNyttForAdmin: return 'Dette er nytt for administrator';
            case Meldingstype.detteErNyttForTesting: return 'Dette er nytt for testing';
        }
    }
}

@Injectable({providedIn: 'root'})
export class AdministrasjonMeldingerAvhengigheter {
    constructor(private ressurs: SystemmeldingRessursClient) {}

    finnAlle(): Promise<SystemMeldingModell[]> {
        return this.ressurs.finnAlle().somPromise();
    }

    opprett(spesifikasjon: OpprettSystemMeldingSpesifikasjon): Promise<SystemMeldingModell> {
        return this.ressurs.opprett(spesifikasjon).somPromise();
    }
}

function detteErNyttMelding(meldingstype: Meldingstype | null): boolean {
    return meldingstype === Meldingstype.detteErNyttForMedlem ||
      meldingstype === Meldingstype.detteErNyttForAdmin ||
      meldingstype === Meldingstype.detteErNyttForTesting;
}

function dekodHexHtmlEntiteter(streng: string): string {
    return streng.replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)));
}
